import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { FaRegSquareMinus } from "react-icons/fa6"
import { FaRegSquarePlus } from "react-icons/fa6"
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks/reduxHooks"
import { ChangeEvent, useEffect, useState } from "react"
import { FaLongArrowAltRight } from "react-icons/fa"
import { addCommas } from "@/lib/addCommas"
import Link from "next/link"
import { toast } from "react-toastify"
import { convertShardsToCredits } from "@/app/dashboard/dashboardAction"
import { convertShardsToCreditsPayload } from "@/types/types.dashboard"

type Props = {
    credits: number
}

export const SeasonCurrencyConverterComponent: React.FC<Props> = (props) => {
    const shards = useAppSelector((state) => state.auth.shards)
    const playerTitleId = useAppSelector((state) => state.auth.playerTitleId)
    const [disabled, setDisabled] = useState<boolean>(true)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [amount, setAmount] = useState<number>(1)
    const [open, setOpen] = useState<boolean>(false)
    const dispatch = useAppDispatch()
    const { credits } = props

    const increment = () => {
        if (amount < 1000) {
            setAmount((prev) => prev + 1)
        }
    }

    const decrement = () => {
        if (amount > 1) {
            setAmount((prev) => prev - 1)
        }
    }

    const validator = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (value === "" || /^[0-9\b]+$/.test(value)) {
            let number = parseInt(value, 10)

            // Ensure the number is between 1 and 1000
            if (value === "" || (number >= 1 && number <= 1000)) {
                setAmount(parseInt(value))
            }
        }
    }

    useEffect(() => {
        if (shards < amount) {
            setDisabled(true)
        } else {
            setDisabled(false)
        }
    }, [shards, amount])

    const initConversion = async () => {
        setSubmitting(true)
        try {
            const payload: convertShardsToCreditsPayload = {
                shardAmount: amount,
                playerTitleId: playerTitleId!,
            }
            const response = await convertShardsToCredits(payload)
            if (response.success) {
                const newCredits = credits + amount * 100
                dispatch({ type: "auth/setCredits", payload: newCredits })
                const newShards = shards - amount
                dispatch({ type: "auth/setShards", payload: newShards })
                setOpen(false)
                toast.success(
                    `Successfully converted ${amount} shard${amount > 1 ? "s" : ""} to ${addCommas(
                        amount * 100
                    )} credits`
                )
            }
        } catch (error) {
            console.warn("Error converting shards to credits", error)
            toast.error("There was an error with your conversion. Please try again.")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <AlertDialog open={open}>
            <button
                className="mt-8 transition-all py-1 px-3 hover:glow-yellow font-normal border border-[#EDE636] text-[#EDE636]"
                onClick={() => setOpen((prev) => !prev)}
            >
                CURRENCY CONVERTER
            </button>
            <AlertDialogContent className="bg-black border-none text-white max-w-[400px]">
                <AlertDialogHeader>
                    <AlertDialogTitle className="text-[23px]">Currency Converter</AlertDialogTitle>
                    <section className="font-thin text-[17px]">
                        <div>
                            Convert your shards to credits. If you are low on shards, you can purchase more with{" "}
                            <Link href="/store" target="_blank" className="underline">
                                MCG
                            </Link>{" "}
                            or{" "}
                            <Link href="https://store.metalcore.gg/" target="_blank" className="underline">
                                fiat
                            </Link>
                            .
                        </div>
                        <section className="border-t border-b border-[#555] my-4 py-2">
                            <section className="flex items-center justify-center">
                                <div>
                                    {amount} Shard{amount > 1 ? "s" : ""}
                                </div>
                                <div>
                                    <FaLongArrowAltRight className="relative top-[-1px] ml-3 mr-2" />
                                </div>
                                <div>{addCommas(amount * 100)} Credits</div>
                            </section>
                            <section className="flex items-center justify-center mt-2">
                                <div>
                                    <button
                                        className={`cursor-pointer ${
                                            amount === 1 ? "hover:text-red-500" : "hover:text-primary"
                                        } transition-all`}
                                        onClick={() => decrement()}
                                    >
                                        <FaRegSquareMinus className="text-[34px]" />
                                    </button>
                                </div>
                                <div>
                                    <input
                                        type="number"
                                        value={amount}
                                        className="h-[33px] text-[21px] relative top-[-3px] px-2 mx-2 rounded text-center text-black font-bold disabled:text-black w-[80px]"
                                        onChange={(e) => validator(e)}
                                    />
                                </div>
                                <div>
                                    <button
                                        className={`cursor-pointer ${
                                            amount === 1000 ? "hover:text-red-500" : "hover:text-primary"
                                        } transition-all`}
                                        onClick={() => increment()}
                                    >
                                        <FaRegSquarePlus className="text-[34px]" />
                                    </button>
                                </div>
                            </section>
                        </section>
                    </section>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel className="hover:text-black hover:border-primary" onClick={() => setOpen(false)}>
                        Cancel
                    </AlertDialogCancel>
                    <button
                        className="text-black disabled:cursor-not-allowed bg-primary rounded px-2 disabled:bg-slate-400"
                        disabled={disabled || submitting}
                        onClick={() => initConversion()}
                    >
                        {disabled ? "Not enough shards" : submitting ? "Processing" : "Submit"}
                    </button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
